import { useEffect, useMemo, useState } from "react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { Link, createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import {
  Atom,
  Bookmark,
  Clock,
  Dna,
  Flame,
  FlaskConical,
  Search,
  Sigma,
  Sparkles,
  TestTube2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SIMULATIONS, type SimMeta } from "@/lib/simulations/registry";
import { readBookmarks, readRecents, toggleBookmark } from "@/lib/simulations/storage";
import type { SimSubject } from "@/lib/simulations/types";
import { cn } from "@/lib/utils";
import { CreditsSummary } from "@/components/billing/credits-summary";
import { ProfileHeaderButton } from "@/components/app/profile-header-button";
import { ShareButton } from "@/components/app/share-button";

export const Route = createFileRoute("/simulations/")({
  head: () => ({
    ...seo({
      title: "Interactive JEE Simulations — Physics, Chemistry & Maths",
      description:
        "Interactive JEE Main & Advanced simulations for Physics, Chemistry and Maths — move a slider and watch the concept respond in real time.",
      path: "/simulations",
    }),
    scripts: [
      breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Simulations", path: "/simulations" },
      ]),
    ],
  }),
  component: SimulationHub,
});

const FILTERS: ("All" | SimSubject)[] = ["All", "Physics", "Chemistry", "Mathematics", "Biology"];

/** One restrained accent per subject — used only for the small card icon. */
const SUBJECT_ICON: Record<SimSubject, LucideIcon> = {
  Physics: Atom,
  Chemistry: TestTube2,
  Mathematics: Sigma,
  Biology: Dna,
};

const HUB_HEADERS: Record<"All" | SimSubject, { eyebrow: string; title: string; sub: string }> = {
  All: {
    eyebrow: "Interactive lab",
    title: "Simulations",
    sub: "Move a parameter, watch the concept respond. Concept labs for JEE Main & Advanced.",
  },
  Physics: {
    eyebrow: "Physics lab",
    title: "Physics Simulations",
    sub: "Mechanics, waves, electromagnetism and modern physics — in motion.",
  },
  Chemistry: {
    eyebrow: "Virtual laboratory",
    title: "Chemistry Virtual Lab",
    sub: "Burners, beakers, cells and mechanisms you can actually run.",
  },
  Biology: {
    eyebrow: "NEET biology lab",
    title: "Biology Simulations",
    sub: "Living systems in motion — circulation and nerve impulses you can play with.",
  },
  Mathematics: {
    eyebrow: "Visual maths",
    title: "Mathematics Simulations",
    sub: "Understand mathematics through interactive visuals, not static graphs.",
  },
};

function SimCard({
  sim,
  index,
  bookmarked,
  onBookmark,
}: {
  sim: SimMeta;
  index: number;
  bookmarked: boolean;
  onBookmark: (id: string) => void;
}) {
  const Icon = SUBJECT_ICON[sim.subject] ?? Atom;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.25), type: "spring", stiffness: 220, damping: 26 }}
      className="group relative flex h-full flex-col rounded-2xl border border-hairline bg-card/50 backdrop-blur-xl transition-colors hover:border-primary/40 hover:bg-card/70"
    >
      <Link
        to="/simulations/$simId"
        params={{ simId: sim.id }}
        className="flex h-full flex-col gap-2 p-4 pr-12"
      >
        <div className="flex items-center gap-2">
          <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-primary/25 bg-primary/10">
            <Icon className="size-4 text-primary" />
          </span>
          <p className="min-w-0 truncate text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {sim.subject} · {sim.chapter}
          </p>
        </div>
        <h3 className="text-[15px] font-semibold leading-snug tracking-tight">{sim.title}</h3>
        <p className="line-clamp-2 text-[12.5px] leading-relaxed text-muted-foreground">
          {sim.blurb}
        </p>
        <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-2">
          <span className="rounded-md border border-hairline bg-surface/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
            {sim.difficulty}
          </span>
          <span className="flex items-center gap-1 rounded-md border border-hairline bg-surface/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
            <Clock className="size-3" /> {sim.minutes} min
          </span>
        </div>
      </Link>
      <button
        type="button"
        aria-label={bookmarked ? `Remove ${sim.title} from saved` : `Save ${sim.title}`}
        aria-pressed={bookmarked}
        onClick={() => onBookmark(sim.id)}
        className={cn(
          "absolute right-2 top-2 grid size-9 place-items-center rounded-lg border transition-colors",
          bookmarked
            ? "border-primary/40 bg-primary/10"
            : "border-transparent text-muted-foreground hover:border-hairline hover:bg-surface/60",
        )}
      >
        <Bookmark className={cn("size-4", bookmarked ? "fill-primary text-primary" : "")} />
      </button>
    </motion.div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-8">
      <h2 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        <Icon className="size-3.5" /> {title}
      </h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:gap-4">{children}</div>
    </section>
  );
}

function SimulationHub() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"All" | SimSubject>("All");
  const [bookmarks, setBookmarks] = useState<string[]>([]);
  const [recentIds, setRecentIds] = useState<string[]>([]);

  useEffect(() => {
    setBookmarks(readBookmarks());
    setRecentIds(readRecents().map((r) => r.id));
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SIMULATIONS.filter((sim) => {
      if (filter !== "All" && sim.subject !== filter) return false;
      if (!q) return true;
      return (
        sim.title.toLowerCase().includes(q) ||
        sim.chapter.toLowerCase().includes(q) ||
        sim.blurb.toLowerCase().includes(q) ||
        sim.tags.some((tag) => tag.includes(q))
      );
    });
  }, [query, filter]);

  const featured = useMemo(
    () => [...SIMULATIONS].sort((a, b) => b.popularity - a.popularity).slice(0, 3),
    [],
  );
  const recents = recentIds
    .map((id) => SIMULATIONS.find((s) => s.id === id))
    .filter((s): s is SimMeta => Boolean(s))
    .slice(0, 3);
  const saved = SIMULATIONS.filter((s) => bookmarks.includes(s.id));

  const onBookmark = (id: string) => setBookmarks(toggleBookmark(id));
  const searching = query.trim().length > 0 || filter !== "All";

  return (
    <div className="page-shell px-5 pb-8 pt-8 lg:px-8">
      <motion.header
        key={filter}
        initial={{ y: -10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end"
      >
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {HUB_HEADERS[filter].eyebrow}
          </p>
          <h1 className="mt-1.5 text-[26px] font-bold tracking-tight lg:text-[34px]">
            {HUB_HEADERS[filter].title}
          </h1>
          <p className="mt-1.5 max-w-xl text-[13.5px] leading-relaxed text-muted-foreground">
            {HUB_HEADERS[filter].sub}
          </p>
        </div>
        <div className="flex items-center justify-between gap-3 lg:justify-end">
          <CreditsSummary className="lg:min-w-[260px]" />
          <ShareButton />
          <div className="hidden lg:block">
            <ProfileHeaderButton />
          </div>
        </div>

      </motion.header>

      <div className="mt-6 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
        <label className="flex items-center gap-2.5 rounded-xl border border-hairline bg-card/50 px-4 py-3 backdrop-blur-xl focus-within:border-primary/40">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder="Search simulations, chapters or tags…"
            aria-label="Search simulations"
            className="w-full bg-transparent text-[13.5px] outline-none placeholder:text-muted-foreground"
          />
        </label>
        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          {FILTERS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setFilter(item)}
              className={cn(
                "inline-flex h-11 shrink-0 items-center rounded-xl border px-4 text-[12.5px] font-medium transition-colors",
                filter === item
                  ? "border-primary/45 bg-primary/12 text-primary"
                  : "border-hairline bg-card/40 text-muted-foreground hover:text-foreground",
              )}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <Link
        to="/labs"
        className="mt-4 flex items-center gap-3 rounded-2xl border border-hairline bg-card/50 px-4 py-3.5 backdrop-blur-xl transition-colors hover:border-primary/40"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-lg border border-primary/25 bg-primary/10">
          <FlaskConical className="size-4 text-primary" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] font-semibold tracking-tight">Sim Labs</span>
          <span className="block text-[12.5px] text-muted-foreground">
            Hands-on physics experiments you drag, tilt and measure.
          </span>
        </span>
        <span className="hidden text-[12px] font-medium text-primary sm:block">Open →</span>
      </Link>

      {!searching ? (
        <>
          <Section icon={Flame} title="Most explored">
            {featured.map((sim, i) => (
              <SimCard
                key={sim.id}
                sim={sim}
                index={i}
                bookmarked={bookmarks.includes(sim.id)}
                onBookmark={onBookmark}
              />
            ))}
          </Section>

          {recents.length > 0 ? (
            <Section icon={Clock} title="Continue">
              {recents.map((sim, i) => (
                <SimCard
                  key={sim.id}
                  sim={sim}
                  index={i}
                  bookmarked={bookmarks.includes(sim.id)}
                  onBookmark={onBookmark}
                />
              ))}
            </Section>
          ) : null}

          {saved.length > 0 ? (
            <Section icon={Bookmark} title="Saved">
              {saved.map((sim, i) => (
                <SimCard key={sim.id} sim={sim} index={i} bookmarked onBookmark={onBookmark} />
              ))}
            </Section>
          ) : null}
        </>
      ) : null}

      <Section icon={Sparkles} title={searching ? `${results.length} results` : "All simulations"}>
        {results.map((sim, i) => (
          <SimCard
            key={sim.id}
            sim={sim}
            index={i}
            bookmarked={bookmarks.includes(sim.id)}
            onBookmark={onBookmark}
          />
        ))}
      </Section>
      {results.length === 0 ? (
        <div className="mt-3 rounded-2xl border border-hairline bg-card/50 p-8 text-center">
          <p className="font-semibold">No simulation matches that</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Try a chapter name like &ldquo;waves&rdquo; or clear the subject filter.
          </p>
        </div>
      ) : null}
    </div>
  );
}
